'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import type {
  DreamMemory,
  DreamMemoryContextValue,
  DreamMemoryVisual,
  SmartRefreshPrompt,
} from './types';
import { SMART_REFRESH_TRIGGERS } from './types';
import type { BusinessProfile } from '@/lib/businessProfile/types';

// ─── Storage Key ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'dreamMemory_v1';

// ─── Default Memory ───────────────────────────────────────────────────────────

function createDefaultMemory(dreamText: string, projectId?: string): DreamMemory {
  const now = new Date().toISOString();
  return {
    dreamText,
    identity: {},
    audience: {},
    brand: {},
    products: {},
    strategy: {},
    generatedVisuals: [],
    favoriteVisuals: [],
    project: { projectStatus: 'draft' },
    sections: {
      generatedContent: {},
      generatedSections: new Set(),
      savedSections: new Set(),
      sectionVersions: {},
    },
    edits: {
      userEditedFields: new Set(),
      lastEditedAt: {},
    },
    createdAt: now,
    updatedAt: now,
    projectId,
  };
}

// ─── Serialization helpers (Sets are not JSON-serializable) ───────────────────

function serializeMemoryInternal(memory: DreamMemory): object {
  return {
    ...memory,
    sections: {
      ...memory.sections,
      generatedSections: Array.from(memory.sections.generatedSections),
      savedSections: Array.from(memory.sections.savedSections),
    },
    edits: {
      ...memory.edits,
      userEditedFields: Array.from(memory.edits.userEditedFields),
    },
  };
}

function deserializeMemory(data: any): DreamMemory {
  return {
    ...data,
    sections: {
      ...data.sections,
      generatedSections: new Set(data.sections?.generatedSections ?? []),
      savedSections: new Set(data.sections?.savedSections ?? []),
      generatedContent: data.sections?.generatedContent ?? {},
      sectionVersions: data.sections?.sectionVersions ?? {},
    },
    edits: {
      ...data.edits,
      userEditedFields: new Set(data.edits?.userEditedFields ?? []),
      lastEditedAt: data.edits?.lastEditedAt ?? {},
    },
    generatedVisuals: data.generatedVisuals ?? [],
    favoriteVisuals: data.favoriteVisuals ?? [],
    project: data.project ?? { projectStatus: 'draft' },
    identity: data.identity ?? {},
    audience: data.audience ?? {},
    brand: data.brand ?? {},
    products: data.products ?? {},
    strategy: data.strategy ?? {},
  };
}

// ─── Set nested value by dot-path ─────────────────────────────────────────────

function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split('.');
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const head = keys[0];
  const restPath = keys.slice(1).join('.');
  return {
    ...obj,
    [head]: setNestedValue(obj[head] ?? {}, restPath, value),
  };
}

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

// ─── Context ──────────────────────────────────────────────────────────────────

const DreamMemoryContext = createContext<DreamMemoryContextValue | null>(null);

export function DreamMemoryProvider({ children }: { children: React.ReactNode }) {
  const [memory, setMemory] = useState<DreamMemory | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingRefreshPrompt, setPendingRefreshPrompt] = useState<SmartRefreshPrompt | null>(null);

  // Track previous values for change detection
  const prevValuesRef = useRef<Record<string, any>>({});

  // ── Persist to sessionStorage on change ──────────────────────────────────
  useEffect(() => {
    if (memory) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(serializeMemoryInternal(memory)));
      } catch {
        // storage quota exceeded — silently ignore
      }
    }
  }, [memory]);

  // ── Restore from sessionStorage on mount ─────────────────────────────────
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setMemory(deserializeMemory(parsed));
        setIsInitialized(true);
      }
    } catch {
      // corrupted storage — ignore
    }
  }, []);

  // ── Init ──────────────────────────────────────────────────────────────────
  const initMemory = useCallback((dreamText: string, projectId?: string) => {
    setMemory(prev => {
      if (prev && prev.dreamText === dreamText) {
        // Same dream text: keep memory unless projectId clearly switched
        if (!projectId || !prev.projectId || prev.projectId === projectId) {
          if (projectId && !prev.projectId) {
            return { ...prev, projectId };
          }
          return prev;
        }
      }
      return createDefaultMemory(dreamText, projectId);
    });
    setIsInitialized(true);
  }, []);

  // ── Update top-level field ────────────────────────────────────────────────
  const updateMemory = useCallback(<K extends keyof DreamMemory>(
    field: K,
    value: DreamMemory[K],
    isUserEdit = false
  ) => {
    setMemory(prev => {
      if (!prev) return prev;
      const now = new Date().toISOString();
      const updated: DreamMemory = {
        ...prev,
        [field]: value,
        updatedAt: now,
      };
      if (isUserEdit) {
        updated.edits = {
          ...prev.edits,
          userEditedFields: new Set([...prev.edits.userEditedFields, field as string]),
          lastEditedAt: { ...prev.edits.lastEditedAt, [field as string]: now },
        };
      }
      return updated;
    });
  }, []);

  // ── Update nested field by dot-path ──────────────────────────────────────
  const updateMemoryField = useCallback((path: string, value: any, isUserEdit = false) => {
    setMemory(prev => {
      if (!prev) return prev;
      const now = new Date().toISOString();
      const updated = setNestedValue(prev, path, value) as DreamMemory;
      updated.updatedAt = now;

      if (isUserEdit) {
        updated.edits = {
          ...prev.edits,
          userEditedFields: new Set([...prev.edits.userEditedFields, path]),
          lastEditedAt: { ...prev.edits.lastEditedAt, [path]: now },
        };

        // ── Smart change detection ──────────────────────────────────────
        const topKey = path.split('.')[0];
        const trigger = SMART_REFRESH_TRIGGERS[topKey] ?? SMART_REFRESH_TRIGGERS[path];
        if (trigger) {
          const prevVal = getNestedValue(prev, path);
          const newVal = value;
          if (prevVal !== undefined && prevVal !== newVal && String(prevVal).trim() !== String(newVal).trim()) {
            setPendingRefreshPrompt({
              changedField: path,
              changedFieldLabel: trigger.label,
              relatedSections: trigger.relatedSections,
              message: buildRefreshMessage(trigger.label, trigger.relatedSections),
            });
          }
        }
      }

      return updated;
    });
  }, []);

  // ── Sync from BusinessProfile ─────────────────────────────────────────────
  const syncFromBusinessProfile = useCallback((profile: BusinessProfile) => {
    setMemory(prev => {
      if (!prev) return prev;
      const now = new Date().toISOString();

      // Only update fields that the user hasn't manually edited
      const userEdited = prev.edits.userEditedFields;

      const identity = { ...prev.identity };
      if (!userEdited.has('identity.businessType')) identity.businessType = profile.business_type;
      if (!userEdited.has('identity.industry')) identity.industry = profile.industry;
      if (!userEdited.has('identity.niche')) identity.niche = profile.niche;
      if (!userEdited.has('identity.preferredLanguage')) identity.preferredLanguage = profile.language;

      const audience = { ...prev.audience };
      if (!userEdited.has('audience.targetAudience')) audience.targetAudience = profile.audience?.primary;
      if (!userEdited.has('audience.secondaryAudience')) audience.secondaryAudience = profile.audience?.secondary;
      if (!userEdited.has('audience.painPoints')) audience.painPoints = profile.audience?.pain_points;
      if (!userEdited.has('audience.desires')) audience.desires = profile.audience?.desires;

      const brand = { ...prev.brand };
      if (!userEdited.has('brand.brandPersonality')) brand.brandPersonality = profile.brand_personality;
      if (!userEdited.has('brand.brandArchetype')) brand.brandArchetype = profile.brand_archetype;
      if (!userEdited.has('brand.tone')) brand.tone = profile.tone;
      if (!userEdited.has('brand.colors')) brand.colors = profile.color_direction;
      if (!userEdited.has('brand.typography')) brand.typography = profile.typography_direction;
      if (!userEdited.has('brand.preferredVisualStyle')) brand.preferredVisualStyle = profile.visual_style;
      if (!userEdited.has('brand.imageStyle')) brand.imageStyle = profile.image_style;

      const products = { ...prev.products };
      if (!userEdited.has('products.pricingLevel')) products.pricingLevel = profile.pricing_level;
      if (!userEdited.has('products.monetizationMethods')) products.monetizationMethods = profile.monetization_methods;
      if (!userEdited.has('products.products')) {
        products.products = profile.recommended_products_services?.map(p => ({
          name: p.name,
          description: p.description,
          priceRange: p.price_range,
        }));
      }

      const strategy = { ...prev.strategy };
      if (!userEdited.has('strategy.marketingChannels')) strategy.marketingChannels = profile.marketing_channels;
      if (!userEdited.has('strategy.launchPhases')) {
        strategy.launchPhases = {
          phase1: profile.launch_strategy?.phase1,
          phase2: profile.launch_strategy?.phase2,
          phase3: profile.launch_strategy?.phase3,
          quickWins: profile.launch_strategy?.quick_wins,
        };
      }

      const project = { ...prev.project };
      if (!userEdited.has('project.projectGoals')) {
        project.projectGoals = [
          ...(profile.business_goals?.short_term ?? []),
          ...(profile.business_goals?.long_term ?? []),
        ];
      }

      return {
        ...prev,
        identity,
        audience,
        brand,
        products,
        strategy,
        project,
        updatedAt: now,
      };
    });
  }, []);

  // ── Record generated section ──────────────────────────────────────────────
  const recordGeneratedSection = useCallback((sectionId: string, content: string) => {
    setMemory(prev => {
      if (!prev) return prev;
      const prevContent = prev.sections.generatedContent[sectionId];
      const versions = prev.sections.sectionVersions[sectionId] ?? [];
      const newVersions = prevContent
        ? [prevContent, ...versions].slice(0, 5)
        : versions;

      return {
        ...prev,
        sections: {
          ...prev.sections,
          generatedContent: { ...prev.sections.generatedContent, [sectionId]: content },
          generatedSections: new Set([...prev.sections.generatedSections, sectionId]),
          sectionVersions: { ...prev.sections.sectionVersions, [sectionId]: newVersions },
        },
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Record saved section ──────────────────────────────────────────────────
  const recordSavedSection = useCallback((sectionId: string) => {
    setMemory(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          savedSections: new Set([...prev.sections.savedSections, sectionId]),
        },
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Add generated visual ──────────────────────────────────────────────────
  const addGeneratedVisual = useCallback((visual: DreamMemoryVisual) => {
    setMemory(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        generatedVisuals: [...prev.generatedVisuals, visual],
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Toggle favorite visual ────────────────────────────────────────────────
  const toggleFavoriteVisual = useCallback((visualId: string) => {
    setMemory(prev => {
      if (!prev) return prev;
      const isFav = prev.favoriteVisuals.includes(visualId);
      return {
        ...prev,
        favoriteVisuals: isFav
          ? prev.favoriteVisuals.filter(id => id !== visualId)
          : [...prev.favoriteVisuals, visualId],
        generatedVisuals: prev.generatedVisuals.map(v =>
          v.id === visualId ? { ...v, isFavorite: !isFav } : v
        ),
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Check if field is user-edited ─────────────────────────────────────────
  const isUserEdited = useCallback((fieldPath: string): boolean => {
    return memory?.edits.userEditedFields.has(fieldPath) ?? false;
  }, [memory]);

  // ── Build AI context string ───────────────────────────────────────────────
  const getAIContext = useCallback((): string => {
    if (!memory) return '';
    const parts: string[] = [];

    if (memory.identity.businessName) parts.push(`Business Name: ${memory.identity.businessName}`);
    if (memory.identity.businessType) parts.push(`Business Type: ${memory.identity.businessType}`);
    if (memory.identity.industry) parts.push(`Industry: ${memory.identity.industry}`);
    if (memory.identity.niche) parts.push(`Niche: ${memory.identity.niche}`);
    if (memory.audience.targetAudience) parts.push(`Target Audience: ${memory.audience.targetAudience}`);
    if (memory.audience.customerPersona) parts.push(`Customer Persona: ${memory.audience.customerPersona}`);
    if (memory.brand.tone) parts.push(`Brand Tone: ${memory.brand.tone}`);
    if (memory.brand.brandPersonality?.length) parts.push(`Brand Personality: ${memory.brand.brandPersonality.join(', ')}`);
    if (memory.brand.colors?.primary) parts.push(`Brand Colors: ${memory.brand.colors.primary}, ${memory.brand.colors.secondary ?? ''}, ${memory.brand.colors.accent ?? ''}`);
    if (memory.brand.preferredVisualStyle) parts.push(`Visual Style: ${memory.brand.preferredVisualStyle}`);
    if (memory.products.pricingLevel) parts.push(`Pricing Level: ${memory.products.pricingLevel}`);
    if (memory.strategy.marketingChannels?.length) parts.push(`Marketing Channels: ${memory.strategy.marketingChannels.join(', ')}`);
    if (memory.project.dreamScore) parts.push(`Dream Score™: ${memory.project.dreamScore}/100`);
    if (memory.project.projectStatus) parts.push(`Project Status: ${memory.project.projectStatus}`);

    if (parts.length === 0) return '';
    return (
      `\n\n[Dream Memory™ Context — facts about THIS project only; ignore any prior projects]\n` +
      `${parts.join('\n')}\n` +
      `Write all new content in the language required by the system/user language rules, even if some context above is in another language.`
    );
  }, [memory]);

  // ── Check if section generated ────────────────────────────────────────────
  const isSectionGenerated = useCallback((sectionId: string): boolean => {
    return memory?.sections.generatedSections.has(sectionId) ?? false;
  }, [memory]);

  // ── Get section content ───────────────────────────────────────────────────
  const getSectionContent = useCallback((sectionId: string): string | null => {
    return memory?.sections.generatedContent[sectionId] ?? null;
  }, [memory]);

  // ── Dismiss refresh prompt ────────────────────────────────────────────────
  const dismissRefreshPrompt = useCallback(() => {
    setPendingRefreshPrompt(null);
  }, []);

  // ── Accept refresh prompt ─────────────────────────────────────────────────
  const acceptRefreshPrompt = useCallback(() => {
    pendingRefreshPrompt?.onAccept?.();
    setPendingRefreshPrompt(null);
  }, [pendingRefreshPrompt]);

  // ── Load from persisted data ──────────────────────────────────────────────
  const loadMemory = useCallback((data: Partial<DreamMemory>) => {
    // Replace memory entirely — never merge with a previous project's state
    const base = createDefaultMemory(data.dreamText ?? '', data.projectId);
    setMemory(deserializeMemory({ ...base, ...data }));
    setIsInitialized(true);
  }, []);

  // ── Serialize for persistence ─────────────────────────────────────────────
  const serializeMemory = useCallback((): object => {
    if (!memory) return {};
    return serializeMemoryInternal(memory);
  }, [memory]);

  // ── Clear memory ──────────────────────────────────────────────────────────
  const clearMemory = useCallback(() => {
    setMemory(null);
    setIsInitialized(false);
    setPendingRefreshPrompt(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  const value: DreamMemoryContextValue = {
    memory,
    isInitialized,
    initMemory,
    updateMemory,
    updateMemoryField,
    syncFromBusinessProfile,
    recordGeneratedSection,
    recordSavedSection,
    addGeneratedVisual,
    toggleFavoriteVisual,
    isUserEdited,
    getAIContext,
    isSectionGenerated,
    getSectionContent,
    pendingRefreshPrompt,
    dismissRefreshPrompt,
    acceptRefreshPrompt,
    loadMemory,
    serializeMemory,
    clearMemory,
  };

  return (
    <DreamMemoryContext.Provider value={value}>
      {children}
    </DreamMemoryContext.Provider>
  );
}

export function useDreamMemory(): DreamMemoryContextValue {
  const ctx = useContext(DreamMemoryContext);
  if (!ctx) {
    throw new Error('useDreamMemory must be used within DreamMemoryProvider');
  }
  return ctx;
}

// ─── Helper: build smart refresh message ─────────────────────────────────────

function buildRefreshMessage(fieldLabel: string, relatedSections: string[]): string {
  const sectionLabels = relatedSections
    .slice(0, 3)
    .map(s => sectionIdToLabel(s))
    .join(', ');
  return `You changed your ${fieldLabel}. Would you like to refresh your ${sectionLabels}?`;
}

function sectionIdToLabel(id: string): string {
  const map: Record<string, string> = {
    dreamSummary: 'Dream Summary',
    vision: 'Vision',
    brandKit: 'Brand Kit',
    brand: 'Brand Kit',
    idealCustomer: 'Ideal Customer',
    contentStrategy: 'Content Strategy',
    content: 'Content Strategy',
    launchStrategy: 'Launch Strategy',
    launchPlan: 'Launch Plan',
    businessModel: 'Business Model',
    businessOpportunities: 'Business Opportunities',
    roadmap90: '90-Day Roadmap',
    aiStack: 'AI Stack',
    products: 'Products & Services',
    riskAnalysis: 'Risk Analysis',
    quickWins: 'Quick Wins',
    todaysMission: "Today's Mission",
  };
  return map[id] ?? id;
}
